import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL || "";

const client = axios.create({
  baseURL: BASE,
  timeout: 120000,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

export const WORKGROUP_KEY = "rulesqa.workgroup";
const workgroupHeader = () => {
  const wg = localStorage.getItem(WORKGROUP_KEY);
  return wg ? { "X-Workgroup-Id": wg } : {};
};
// Headers for raw fetch() calls (cookies go via credentials: "include"; the LLM key lives on the server).
const fetchHeaders = () => ({ "Content-Type": "application/json", ...workgroupHeader() });

// Only used to test a key before saving it; normal calls use the key stored on the account.
const llmHeaders = (s) => (s?.apiKey ? { "X-LLM-Provider": s.provider, "X-LLM-Model": s.model, "X-LLM-Key": s.apiKey } : {});

client.interceptors.request.use((config) => {
  for (const [k, v] of Object.entries(workgroupHeader())) {
    if (!config.headers[k]) config.headers[k] = v;
  }
  return config;
});

// Access tokens are short-lived: on 401 refresh the session once, then retry; otherwise signal sign-out.
let refreshing = null;
client.interceptors.response.use(null, async (error) => {
  const cfg = error.config || {};
  const url = cfg.url || "";
  if (error.response?.status !== 401 || cfg._retried || url.includes("/api/auth/") || url.includes("/api/invites/public/") || url.includes("/api/auth/")) throw error;
  refreshing = refreshing || axios.post(`${BASE}/api/auth/refresh`, null, { withCredentials: true }).finally(() => { refreshing = null; });
  try {
    await refreshing;
  } catch (e) {
    window.dispatchEvent(new Event("auth-expired"));
    throw error;
  }
  return client({ ...cfg, _retried: true });
});

async function authFetch(url, opts = {}) {
  const run = () => fetch(url, { ...opts, credentials: "include", headers: { ...fetchHeaders(), ...(opts.headers || {}) } });
  let res = await run();
  if (res.status === 401) {
    const r = await fetch(`${BASE}/api/auth/refresh`, { method: "POST", credentials: "include" });
    if (!r.ok) {
      window.dispatchEvent(new Event("auth-expired"));
      return res;
    }
    res = await run();
  }
  return res;
}

async function startError(res, label) {
  const body = await res.text().catch(() => "");
  let detail = body;
  try { detail = JSON.parse(body).detail || body; } catch (e) { /* plain-text body */ }
  return new Error(res.status === 412 ? detail : `${label} HTTP ${res.status}: ${detail}`);
}

/**
 * Lightweight retry wrapper for one-shot axios requests that hit ingress.
 * Retries on transient gateway statuses (502/503/504/408/429) and any
 * network error, with bounded exponential backoff. Skipped on real client
 * errors (400/401/403/404 on a route we know exists is still surfaced —
 * but Cloudflare's transient 404 from "pod briefly unreachable" is also
 * retried because we cannot distinguish the two from the response alone).
 * MAX_ATTEMPTS=4 means up to 3 retries after the initial call.
 */
const RETRYABLE_STATUSES = new Set([404, 408, 429, 502, 503, 504]);
async function withRetry(fn, { maxAttempts = 4, baseDelay = 600 } = {}) {
  let lastErr;
  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      return await fn();
    } catch (e) {
      lastErr = e;
      const status = e?.response?.status;
      const isNetwork = !e?.response; // axios sets no response for network errors
      const retryable = isNetwork || (status && RETRYABLE_STATUSES.has(status));
      if (!retryable || attempt === maxAttempts) throw e;
      const wait = Math.min(4000, baseDelay * Math.pow(2, attempt - 1));
      await new Promise((r) => setTimeout(r, wait));
    }
  }
  throw lastErr;
}

/** Build a URL to a rulebook page PNG (1-indexed). Cached server-side. */
export const rulebookPageUrl = (page) =>
  `${BASE}/api/rulebook/pages/${page}.png`;

/** Build a URL to a Steamroller page PNG (1-indexed). Cached server-side. */
export const steamrollerPageUrl = (page) =>
  `${BASE}/api/steamroller/pages/${page}.png`;

/** Module-level cache so multiple KeywordChips for the same term share one
 *  in-flight request and reuse the resolved payload. Keyed by lowercase term. */
const keywordLookupCache = new Map();

// PDF total pages per source — fetched once via /api/health and cached.
let _healthPromise = null;
export async function getPdfTotalPages() {
  if (!_healthPromise) {
    _healthPromise = client
      .get("/api/health")
      .then((r) => r.data?.pdf_total_pages || { rulebook: 0, steamroller: 0 })
      .catch(() => ({ rulebook: 0, steamroller: 0 }));
  }
  return _healthPromise;
}

/**
 * Resilient polling helper. Retries transient HTTP errors (502/503/504) and
 * network failures with bounded backoff. Aborts cleanly when `signal.cancelled`
 * is true. Each successful response is forwarded to `onResponse(jobJson)`;
 * the caller decides when polling is done (returns truthy from onResponse to
 * stop, or throws).
 */
async function pollJob(jobId, statusUrl, onResponse, signal, opts = {}) {
  const baseDelay = opts.delay || 800;
  let attempt = 0;       // consecutive transient failures
  const MAX_TRANSIENT = 8;
  while (true) {
    if (signal && signal.cancelled) {
      const err = new Error("cancelled");
      err.cancelled = true;
      throw err;
    }
    await new Promise((r) => setTimeout(r, baseDelay));
    let sres;
    try {
      sres = await authFetch(`${statusUrl}/${jobId}`);
    } catch (netErr) {
      attempt += 1;
      if (attempt > MAX_TRANSIENT) throw new Error(`Status network error: ${netErr.message}`);
      await new Promise((r) => setTimeout(r, Math.min(5000, 500 * attempt)));
      continue;
    }
    if (!sres.ok) {
      // Retry transient gateway errors (Cloudflare/ingress hiccups). 404 is
      // included because edge proxies can briefly return 404 when the pod
      // is unreachable during ingress re-registration.
      if ([404, 502, 503, 504].includes(sres.status)) {
        attempt += 1;
        if (attempt > MAX_TRANSIENT) throw new Error(`Status HTTP ${sres.status} after ${attempt} retries`);
        await new Promise((r) => setTimeout(r, Math.min(5000, 500 * attempt)));
        continue;
      }
      throw new Error(`Status HTTP ${sres.status}`);
    }
    attempt = 0;
    const job = await sres.json();
    const ret = onResponse(job);
    if (ret) return ret;
  }
}

/**
 * Poll-based compare-mode analyze (two proposals in parallel + LLM
 * comparison paragraph). onProgress receives the same job dict shape as
 * analyzeStream but with stages_a/stages_b/partial_a/partial_b/comparison.
 * Resolves with the final {result_a, result_b, comparison, job_id} payload.
 * `signal` (optional) — { cancelled: bool } toggled true to abort polling.
 */
export async function analyzeCompareStream(text_a, text_b, onProgress, rule_type = null, model_types = null, intent = null, signal = null, rule_stats = null) {
  const startRes = await authFetch(`${BASE}/api/analyze/compare/start`, {
    method: "POST",
    body: JSON.stringify({ text_a, text_b, rule_type, model_types: model_types || [], intent: intent || null, rule_stats }),
  });
  if (!startRes.ok) throw await startError(startRes, "Compare start");
  const { job_id } = await startRes.json();

  let lastVersion = -1;
  return pollJob(job_id, `${BASE}/api/analyze/status`, (job) => {
    if (job.version !== lastVersion) {
      lastVersion = job.version;
      onProgress(job, job_id);
    }
    if (job.status === "complete") {
      return {
        result_a: job.result_a,
        result_b: job.result_b,
        comparison: job.comparison && { ...job.comparison, usage: job.usage },
        job_id,
      };
    }
    if (job.status === "cancelled") {
      const err = new Error("cancelled");
      err.cancelled = true;
      throw err;
    }
    if (job.status === "error") throw new Error(job.error || "Compare failed");
    return null;
  }, signal);
}

/**
 * Poll-based analyze. Starts a background job, polls the status endpoint
 * every ~700ms and invokes onProgress(jobState, jobId) until the job completes.
 * Resolves with the final result dict.
 * `signal` (optional) — { cancelled: bool } toggled true to abort polling.
 */
export async function analyzeStream(text, onProgress, rule_type = null, model_types = null, intent = null, signal = null, rule_stats = null) {
  // Kick off the job.
  const startRes = await authFetch(`${BASE}/api/analyze/start`, {
    method: "POST",
    body: JSON.stringify({ text, rule_type, model_types: model_types || [], intent: intent || null, rule_stats }),
  });
  if (!startRes.ok) throw await startError(startRes, "Start");
  const { job_id } = await startRes.json();

  let lastVersion = -1;
  return pollJob(job_id, `${BASE}/api/analyze/status`, (job) => {
    if (job.version !== lastVersion) {
      lastVersion = job.version;
      onProgress(job, job_id);
    }
    if (job.status === "complete") return job.result;
    if (job.status === "cancelled") {
      const err = new Error("cancelled");
      err.cancelled = true;
      throw err;
    }
    if (job.status === "error") throw new Error(job.error || "Analysis failed");
    return null;
  }, signal, { delay: 700 });
}

export const api = {
  health: () => client.get("/api/health").then((r) => r.data),
  listRules: ({ q = "", section = "", limit = 50, offset = 0 } = {}) =>
    client
      .get("/api/rules", { params: { q, section, limit, offset } })
      .then((r) => r.data),
  getRule: (id) => client.get(`/api/rules/${id}`).then((r) => r.data),

  listCards: ({ q = "", faction = "", limit = 30 } = {}) =>
    client
      .get("/api/cards", { params: { q, faction, limit } })
      .then((r) => r.data),
  getCard: (id) => client.get(`/api/cards/${id}`).then((r) => r.data),
  spellOverlap: ({ min_score = 0.85, cross_faction_only = false, kind = "" } = {}) =>
    client.get("/api/spells/overlap", { params: { min_score, cross_faction_only, kind } }).then((r) => r.data),

  listRulebookSections: () =>
    client.get("/api/rulebook/sections").then((r) => r.data),
  getRulebookSection: (id) =>
    client.get(`/api/rulebook/sections/${id}`).then((r) => r.data),

  listSteamrollerSections: () =>
    client.get("/api/steamroller/sections").then((r) => r.data),
  getSteamrollerSection: (id) =>
    client.get(`/api/steamroller/sections/${id}`).then((r) => r.data),

  pageToSection: (page, source = "rulebook") =>
    client.get("/api/page-to-section", { params: { page, source } }).then((r) => r.data),

  suggestRevision: (text, conflicts, rule_type = null, model_types = null, feedback = null, prior_revisions = null, intent = null, rule_stats = null, callout_feedback = null) =>
    withRetry(() => client.post("/api/analyze/suggest-revision", {
      text, conflicts, rule_type,
      model_types: model_types || [],
      feedback: feedback || null,
      prior_revisions: prior_revisions || [],
      intent: intent || null,
      rule_stats,
      callout_feedback: callout_feedback || [],
    })).then((r) => r.data),
  feedbackQuery: (payload) => withRetry(() => client.post("/api/feedback/query", payload)).then((r) => r.data),
  saveFeedback: (payload) => withRetry(() => client.post("/api/feedback", payload)).then((r) => r.data),
  deleteFeedback: (id) => client.delete(`/api/feedback/${id}`).then((r) => r.data),

  llmProviders: () => client.get("/api/llm/providers").then((r) => r.data.providers),
  getMyLlm: () => client.get("/api/me/llm").then((r) => r.data),
  saveMyLlm: (payload) => client.put("/api/me/llm", payload).then((r) => r.data),
  clearMyLlm: () => client.delete("/api/me/llm").then((r) => r.data),
  llmTest: (settings) =>
    client.post("/api/llm/test", null, { headers: llmHeaders(settings), timeout: 60000 }).then((r) => r.data),

  getModelTypes: () => client.get("/api/model-types").then((r) => r.data),

  cancelAnalyze: (job_id) =>
    client.post(`/api/analyze/cancel/${job_id}`).then((r) => r.data).catch(() => null),
  cancelPack: (job_id) =>
    client.post(`/api/packs/cancel/${job_id}`).then((r) => r.data).catch(() => null),

  lookupKeyword: (name) => {
    const key = (name || "").toLowerCase().trim();
    if (!key) return Promise.resolve({ name, rule_id: null, rulebook_section_id: null });
    const cached = keywordLookupCache.get(key);
    if (cached) return cached;
    const p = client
      .get("/api/keywords/lookup", { params: { name } })
      .then((r) => r.data)
      .catch((err) => {
        keywordLookupCache.delete(key); // allow retry on failure
        throw err;
      });
    keywordLookupCache.set(key, p);
    return p;
  },

  analyze: (text, name) =>
    client.post("/api/analyze", { text, name }).then((r) => r.data),

  // Proposals (history)
  listProposals: ({ q = "", tag = "", limit = 100 } = {}) =>
    client.get("/api/proposals", { params: { q, tag, limit } }).then((r) => r.data),
  getProposal: (id) => client.get(`/api/proposals/${id}`).then((r) => r.data),
  saveProposal: (payload) => withRetry(() => client.post("/api/proposals", payload)).then((r) => r.data),
  updateProposal: (id, payload) => withRetry(() => client.patch(`/api/proposals/${id}`, payload)).then((r) => r.data),
  deleteProposal: (id) => client.delete(`/api/proposals/${id}`).then((r) => r.data),
  proposalExportUrl: (q = "", tag = "") => {
    const params = new URLSearchParams({ q, tag });
    const wg = localStorage.getItem(WORKGROUP_KEY);
    if (wg) params.set("wg", wg);
    return `${BASE}/api/proposals/export?${params.toString()}`;
  },

  // Admin
  adminInfo: () => client.get("/api/admin/info").then((r) => r.data),
  adminModels: ({ q = "", faction = "", live_only = false, limit = 200, offset = 0 } = {}) =>
    client.get("/api/admin/models", { params: { q, faction, live_only, limit, offset } })
      .then((r) => r.data),
  adminDiff: ({ from = "", to = "" } = {}) =>
    client.get("/api/admin/diff", { params: { from, to } }).then((r) => r.data),
  uploadRulebook: (file, onProgress) => {
    const form = new FormData();
    form.append("file", file);
    return client.post("/api/admin/rulebook/upload", form, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: onProgress,
      timeout: 5 * 60 * 1000, // 5 minutes
    }).then((r) => r.data);
  },
  uploadSteamroller: (file, onProgress) => {
    const form = new FormData();
    form.append("file", file);
    return client.post("/api/admin/steamroller/upload", form, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: onProgress,
      timeout: 5 * 60 * 1000,
    }).then((r) => r.data);
  },

  // Rule Packs (multi-rule on the same model)
  listPacks: ({ q = "", tag = "", limit = 100 } = {}) =>
    client.get("/api/packs", { params: { q, tag, limit } }).then((r) => r.data),
  cardContext: (id) => withRetry(() => client.get(`/api/cards/${id}/context`)).then((r) => r.data),
  extractCard: (files, imagePaths = []) => {
    const form = new FormData();
    for (const f of files) form.append("files", f);
    form.append("image_paths", JSON.stringify(imagePaths));
    return client.post("/api/cards/extract", form, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 }).then((r) => r.data);
  },
  draftRules: (payload) => client.post("/api/rules/draft", payload, { timeout: 120000 }).then((r) => r.data),
  me: () => client.get("/api/auth/me").then((r) => r.data),
  login: (email, password) => client.post("/api/auth/login", { email, password }).then((r) => r.data),
  logout: () => client.post("/api/auth/logout").then((r) => r.data),
  changePassword: (current_password, new_password) => client.post("/api/auth/password", { current_password, new_password }).then((r) => r.data),
  listUsers: () => client.get("/api/auth/users").then((r) => r.data.items),
  createUser: (payload) => client.post("/api/auth/users", payload).then((r) => r.data),
  getDraft: (scope, since) => client.get(`/api/drafts/${scope}`, { params: { since } }).then((r) => r.data),
  patchDraft: (scope, fields) => client.patch(`/api/drafts/${scope}`, { fields }).then((r) => r.data),
  clearDraft: (scope, prefix = "") => client.post(`/api/drafts/${scope}/clear`, { prefix }).then((r) => r.data),
  jobStatus: (kind, id) => client.get(`/api/${kind === "pack" ? "packs" : "analyze"}/status/${id}`).then((r) => r.data),
  forgotPassword: (email) => client.post("/api/auth/forgot-password", { email }).then((r) => r.data),
  resetPassword: (token, password) => client.post("/api/auth/reset-password", { token, password }).then((r) => r.data),
  signup: (payload) => client.post("/api/auth/signup", payload).then((r) => r.data),
  listBetaCodes: () => client.get("/api/auth/beta-codes").then((r) => r.data.items),
  createBetaCode: (payload) => client.post("/api/auth/beta-codes", payload).then((r) => r.data),
  revokeBetaCode: (id) => client.delete(`/api/auth/beta-codes/${id}`).then((r) => r.data),
  getEmailSettings: () => client.get("/api/admin/email-settings").then((r) => r.data),
  setEmailSender: (sender) => client.put("/api/admin/email-settings", { sender }).then((r) => r.data),
  testEmail: () => client.post("/api/admin/email-settings/test").then((r) => r.data),
  getBrief: () => client.get("/api/brief").then((r) => r.data),
  saveBrief: (content) => client.put("/api/admin/brief", { content }).then((r) => r.data),
  proposeBrief: () => client.post("/api/admin/brief/propose", {}).then((r) => r.data),
  decideBrief: (id, decision, content) => client.post(`/api/admin/brief/proposals/${id}`, { decision, content }).then((r) => r.data),
  recordIconCorrection: (payload) => client.post("/api/icon-corrections", payload).then((r) => r.data),
  listWordingFeedback: () => client.get("/api/feedback/wording").then((r) => r.data.items),
  addWordingFeedback: (payload) => client.post("/api/feedback/wording", payload).then((r) => r.data),
  deleteWordingFeedback: (id) => client.delete(`/api/feedback/wording/${id}`).then((r) => r.data),
  listInvites: () => client.get("/api/auth/invites").then((r) => r.data.items),
  createInvite: (workgroup_ids) => client.post("/api/auth/invites", { workgroup_ids }).then((r) => r.data),
  revokeInvite: (token) => client.delete(`/api/auth/invites/${token}`).then((r) => r.data),
  inviteInfo: (token) => client.get(`/api/invites/public/${token}`).then((r) => r.data),
  acceptInvite: (token, payload) => client.post(`/api/invites/public/${token}/accept`, payload).then((r) => r.data),
  updateUser: (id, payload) => client.patch(`/api/auth/users/${id}`, payload).then((r) => r.data),
  listWorkgroups: () => client.get("/api/workgroups/manage").then((r) => r.data.items),
  createWorkgroup: (name) => client.post("/api/workgroups/manage", { name }).then((r) => r.data),
  deleteUser: (id) => client.delete(`/api/auth/users/${id}`).then((r) => r.data),
  sendResetEmail: (id) => client.post(`/api/auth/users/${id}/send-reset`).then((r) => r.data),
  deleteWorkgroup: (id) => client.delete(`/api/workgroups/manage/${id}`).then((r) => r.data),
  renameWorkgroup: (id, name) => client.patch(`/api/workgroups/manage/${id}`, { name }).then((r) => r.data),
  listComments: (target_type, target_id) => client.get("/api/comments", { params: { target_type, target_id } }).then((r) => r.data.items),
  addComment: (payload) => client.post("/api/comments", payload).then((r) => r.data),
  deleteComment: (id) => client.delete(`/api/comments/${id}`).then((r) => r.data),
  feedbackAll: (params = {}) => client.get("/api/feedback/all", { params }).then((r) => r.data),
  getPack: (id) => client.get(`/api/packs/${id}`).then((r) => r.data),
  savePack: (payload) => withRetry(() => client.post("/api/packs", payload)).then((r) => r.data),
  updatePack: (id, payload) => withRetry(() => client.patch(`/api/packs/${id}`, payload)).then((r) => r.data),
  deletePack: (id) => client.delete(`/api/packs/${id}`).then((r) => r.data),
};

/**
 * Poll-based pack analyze. Kicks off a multi-rule job, polls every ~900ms.
 * onProgress receives (jobState, jobId).
 * Resolves with the final {rules, pack_interactions, job_id} payload.
 * Resilient to transient gateway errors (502/503/504) via pollJob.
 * `signal` (optional) — { cancelled: bool } toggled true to abort polling.
 */
export const uploadUrl = (path) => `${BASE}/api/uploads/${path}`;

export async function analyzePackStream(rules, rule_type, model_types, onProgress, intent = null, signal = null, card = null) {
  const startRes = await authFetch(`${BASE}/api/packs/analyze/start`, {
    method: "POST",
    body: JSON.stringify({ rules, rule_type, model_types: model_types || [], intent: intent || null, card }),
  });
  if (!startRes.ok) throw await startError(startRes, "Pack start");
  const { job_id } = await startRes.json();
  let lastVersion = -1;
  return pollJob(job_id, `${BASE}/api/packs/status`, (job) => {
    if (job.version !== lastVersion) {
      lastVersion = job.version;
      onProgress(job, job_id);
    }
    if (job.status === "complete") {
      return { rules: job.rules, pack_interactions: job.pack_interactions, usage: job.usage, job_id };
    }
    if (job.status === "cancelled") {
      const err = new Error("cancelled");
      err.cancelled = true;
      throw err;
    }
    if (job.status === "error") throw new Error(job.error || "Pack analysis failed");
    return null;
  }, signal, { delay: 900 });
}
